import AddressFindEnumDTO from 'features/shared/enums/dynamic/AddressFindEnumDTO';
import IAddressService from 'features/shared/services/IAddressService';
import { Nullable } from 'features/shared/types/nullable';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React from 'react';
import AsyncSelect from 'react-select/async';
import debounce from "debounce-promise";
import reactSelectStyles from 'features/shared/components/reactSelect/reactselectcustom.module.scss';
import { components, SingleValueProps } from 'react-select';
import { getFormattedZipCode, getStreetSection } from 'features/shared/helpers/AddressFormatter';
import { Country } from 'features/shared/enums/country';
import AddressOption from './AddressOption';
import { useTranslation } from 'react-i18next';

interface Props {
  onChange: (address: Nullable<AddressFindEnumDTO>) => void;
  value?: Nullable<AddressFindEnumDTO>;
  delay?: number;
  minChars?: number;
  placeholder?: string;
  disabled?: boolean;
}

const AddressAutocomplete = ({
  onChange,
  value,
  delay = 500,
  minChars = 3,
  placeholder,
  disabled
}: Props) => {
  // translations
  const tLabels = useTranslation("shared\\components").t;

  // DI
  const addressService = useInjection<IAddressService>(IocTypes.AddressService);

  // helpers
  const findOptions = (text: string): Promise<AddressFindEnumDTO[]> => {
    if (text.length >= minChars) {
      return addressService.findAddresses(text);
    }
    
    return Promise.resolve([]);
  }
  
  const debouncedFindOptions = delay > 0 ? debounce(findOptions, delay) : findOptions;

  const stringifyAddress = (address: Nullable<AddressFindEnumDTO>): string => {
    if (address) {
      let streetSection = getStreetSection(address);
      let zipCode = getFormattedZipCode(address.zipCode, address.countryCode as Country);
      let state = address.stateName ? `, ${address.stateName}` : "";

      return `${streetSection}, ${zipCode} ${address.city}${state}`;
    }
    
    return "";
  }
  
  const noOptionsMessage = (obj: { inputValue: string }): string => {
    if (obj.inputValue.length < minChars) {
      return tLabels("addressAutocomplete.minChars", { minChars });
    }

    return tLabels("addressAutocomplete.noAddress");
  }

  // sub-renders
  const renderSingleValue = (props: SingleValueProps<AddressFindEnumDTO>) => (
    <components.SingleValue {...props}>
       {stringifyAddress(props.data)}
    </components.SingleValue>
  );

  // render
  return (
    <AsyncSelect
      getOptionLabel={(address) => address.city!}
      getOptionValue={stringifyAddress}
      value={value}
      loadOptions={debouncedFindOptions}
      onChange={onChange}
      formatOptionLabel={(address) => AddressOption({address: address, translate: tLabels})}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        SingleValue: renderSingleValue
      }}
      isClearable
      openMenuOnClick={false}
      captureMenuScroll={false}
      placeholder={placeholder ?? tLabels("addressAutocomplete.placeholder")}
      noOptionsMessage={noOptionsMessage}
      loadingMessage={() => tLabels("addressAutocomplete.loading")}
      className={reactSelectStyles.customSelect}
      classNamePrefix="customSelect"
      isDisabled={disabled}
    />
  )
}

export default AddressAutocomplete;
